import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Home, Upload, List, LogOut, User, Settings, Wrench, HelpCircle, DollarSign, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ProfileDropdown } from './ProfileDropdown';
export function WebsiteHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signOut
  } = useAuth();
  const {
    showToast
  } = useToast();
  const handleLogout = async () => {
    setIsMenuOpen(false);
    await signOut();
    navigate('/');
    showToast('Logged out successfully', 'success');
  };
  const isActivePath = (path: string) => location.pathname === path;
  const desktopNavLinks = [{
    path: '/dashboard',
    label: 'Dashboard',
    icon: Home
  }, {
    path: '/upload',
    label: 'Upload',
    icon: Upload
  }, {
    path: '/all-scans',
    label: 'All Scans',
    icon: List
  }];
  const mobileNavLinks = [...desktopNavLinks, {
    path: '/profile',
    label: 'Profile',
    icon: User
  }, {
    path: '/settings',
    label: 'Settings',
    icon: Settings
  }, {
    path: '/premium-tools',
    label: 'Tools',
    icon: Wrench
  }];
  return <header className="sticky top-0 bg-[#050505]/90 backdrop-blur-md border-b border-gray-800 z-50" data-id="element-1009">
      {/* Scanning line at top */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF0000]/50 to-transparent" data-id="element-1010" />

      <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-1011">
        <div className="flex items-center justify-between" data-id="element-1012">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3 group" data-id="element-1013">
            <div className="relative w-8 h-8 flex items-center justify-center bg-[#FF0000]/10 border border-[#FF0000]/30 group-hover:border-[#FF0000] transition-colors" data-id="element-1014">
              <Shield className="w-5 h-5 text-[#FF0000]" data-id="element-1015" />
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-1 h-1 border-t border-l border-[#FF0000]" data-id="element-1016" />
              <div className="absolute bottom-0 right-0 w-1 h-1 border-b border-r border-[#FF0000]" data-id="element-1017" />
            </div>
            <div className="flex flex-col" data-id="element-1018">
              <h1 className="text-lg font-black text-white uppercase tracking-tight leading-none group-hover:text-[#FF0000] transition-colors" data-id="element-1019">
                ToxicDetector
              </h1>
              <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-1020">
                Forensic Analysis Unit
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6" data-id="element-1021">
            <nav className="flex items-center gap-1" data-id="element-1022">
              {desktopNavLinks.map(link => {
              const isActive = isActivePath(link.path);
              return <Link key={link.path} to={link.path} className={`relative px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all group overflow-hidden ${isActive ? 'text-[#FF0000]' : 'text-gray-400 hover:text-white'}`} data-id="element-1023">
                    <span className="relative z-10" data-id="element-1024">{link.label}</span>
                    {isActive && <>
                        <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#FF0000]" data-id="element-1025" />
                        <div className="absolute top-0 left-0 w-full h-full bg-[#FF0000]/5" data-id="element-1026" />
                      </>}
                    {!isActive && <div className="absolute bottom-0 left-0 w-0 h-[1px] bg-gray-600 group-hover:w-full transition-all duration-300" data-id="element-1027" />}
                  </Link>;
            })}
            </nav>

            {/* Divider */}
            <div className="h-8 w-px bg-gray-800 mx-2" data-id="element-1028"></div>

            {/* Profile Dropdown */}
            <ProfileDropdown data-id="element-1029" />
          </div>

          {/* Mobile Hamburger Menu */}
          <button className="md:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors border border-transparent hover:border-gray-700" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" data-id="element-1030">
            {isMenuOpen ? <X size={24} data-id="element-1031" /> : <Menu size={24} data-id="element-1032" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMenuOpen && <nav className="md:hidden mt-4 border-t border-gray-800 pt-4 animate-in slide-in-from-top-2 duration-200 bg-[#0a0a0a] border-b border-gray-800 absolute left-0 right-0 px-6 pb-6 shadow-2xl" data-id="element-1033">
            <div className="space-y-1" data-id="element-1034">
              {mobileNavLinks.map(link => {
            const Icon = link.icon;
            const isActive = isActivePath(link.path);
            return <Link key={link.path} to={link.path} onClick={() => setIsMenuOpen(false)} className={`flex items-center gap-3 px-4 py-3 border border-transparent transition-all ${isActive ? 'bg-[#FF0000]/10 text-[#FF0000] border-[#FF0000]/30' : 'text-gray-400 hover:text-white hover:bg-gray-900 hover:border-gray-800'}`} data-id="element-1035">
                    <Icon size={16} data-id="element-1036" />
                    <span className="text-sm font-bold uppercase tracking-wider" data-id="element-1037">
                      {link.label}
                    </span>
                  </Link>;
          })}
            </div>

            <div className="my-4 border-t border-gray-800" data-id="element-1038"></div>

            <div className="px-4 py-2 flex items-center justify-between text-gray-500 mb-2" data-id="element-1039">
              <div className="flex items-center gap-3" data-id="element-1040">
                <DollarSign size={16} data-id="element-1041" />
                <span className="text-xs font-mono uppercase tracking-wider" data-id="element-1042">
                  Affiliate Program
                </span>
              </div>
              <span className="text-[10px] font-bold bg-gray-900 text-gray-600 px-1.5 py-0.5 border border-gray-800 uppercase" data-id="element-1043">
                SOON
              </span>
            </div>

            <a href="mailto:support@toxicdetector.com" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white hover:bg-gray-900 transition-all border border-transparent hover:border-gray-800" data-id="element-1044">
              <HelpCircle size={16} data-id="element-1045" />
              <span className="text-sm font-bold uppercase tracking-wider" data-id="element-1046">
                Support Protocol
              </span>
            </a>

            <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 mt-2 text-[#FF0000] hover:bg-[#FF0000]/10 transition-all border border-transparent hover:border-[#FF0000]/30" data-id="element-1047">
              <LogOut size={16} data-id="element-1048" />
              <span className="text-sm font-bold uppercase tracking-wider" data-id="element-1049">
                Terminate Session
              </span>
            </button>
          </nav>}
      </div>
    </header>;
}